import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiCheckSquare, FiClock, FiRefreshCw, FiX, FiCheck, FiCircle, FiPlus, FiTrash2, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import { api } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { playDing } from '../utils/beep';
import toast from 'react-hot-toast';

const getTaskCacheKey = (userId) => `futurenote.tasks.cache.${userId}`;

const readLocalJson = (key, fallback) => {
  if (typeof window === 'undefined' || !key) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeLocalJson = (key, value) => {
  if (typeof window === 'undefined' || !key) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore local storage write failures.
  }
};

const toDateInput = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
};

const toDateTimeLocal = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const formatTaskDate = (value) => {
  if (!value) {
    return 'No due date';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No due date';
  }
  return date.toLocaleDateString();
};

const formatReminder = (value) => {
  if (!value) {
    return 'No reminder';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No reminder';
  }
  return date.toLocaleString();
};

const formatTimestamp = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }
  return date.toLocaleString();
};

const getTaskDueTime = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return new Date(`${value.trim()}T23:59:59.999`);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getTaskDueDay = (task) => {
  if (!task || task.isCompleted) {
    return null;
  }

  const dueTime = getTaskDueTime(task.dueDate);
  if (!dueTime) {
    return null;
  }

  return getStartOfDay(dueTime);
};

const isTaskDueToday = (task) => {
  const dueDay = getTaskDueDay(task);
  if (!dueDay) {
    return false;
  }

  const today = getStartOfDay(new Date());
  return dueDay.getTime() === today.getTime();
};

const isTaskOverdue = (task) => {
  const dueDay = getTaskDueDay(task);
  if (!dueDay) {
    return false;
  }

  const today = getStartOfDay(new Date());
  return dueDay.getTime() < today.getTime();
};

const openSettingsConnections = () => {
  window.location.href = '/?open=settings&tab=connections';
};

const TasksDrawer = ({ open, pinned = false, onPinnedChange, onClose }) => {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const [sortBy, setSortBy] = React.useState('date');
  const [sortDirection, setSortDirection] = React.useState('asc');
  const [statusFilter, setStatusFilter] = React.useState('open');
  const [selectedTaskId, setSelectedTaskId] = React.useState(null);
  const [draft, setDraft] = React.useState({
    title: '',
    notes: '',
    dueDate: '',
    reminderAt: ''
  });
  const [newSubtaskTitle, setNewSubtaskTitle] = React.useState('');
  const [subtaskDrafts, setSubtaskDrafts] = React.useState({});
  const taskOwnerKey = user?.id || user?._id || token || null;
  const taskCacheKey = React.useMemo(
    () => (taskOwnerKey ? getTaskCacheKey(taskOwnerKey) : null),
    [taskOwnerKey]
  );
  const initialTasksData = React.useMemo(
    () => readLocalJson(taskCacheKey, null),
    [taskCacheKey]
  );

  const { data, isLoading } = useQuery(
    'tasks',
    async () => {
      const response = await api.get('/tasks');
      return response.data;
    },
    {
      enabled: open,
      initialData: initialTasksData || undefined,
      refetchOnMount: 'always',
      onSuccess: (nextData) => {
        writeLocalJson(taskCacheKey, nextData);
      }
    }
  );

  const syncMutation = useMutation(
    async () => {
      const response = await api.post('/tasks/sync');
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data?.message || 'Task sync started');
        if (data?.started === false) {
          queryClient.invalidateQueries('tasks');
        }
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to sync tasks';
        toast.error(message);
      }
    }
  );

  const updateTaskMutation = useMutation(
    async ({ id, payload }) => {
      const response = await api.put(`/tasks/${id}`, payload);
      return response.data.task;
    },
    {
      onSuccess: (task, variables) => {
        toast.success('Task updated');
        if (variables?.playCompletionSound) {
          playDing();
        }
        queryClient.invalidateQueries('tasks');
        setSelectedTaskId(task.id);
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to update task';
        toast.error(message);
      }
    }
  );

  const tasks = React.useMemo(() => data?.tasks || [], [data]);
  const topLevelTasks = React.useMemo(
    () => tasks.filter((task) => !task.parentTaskId),
    [tasks]
  );
  const reconnectProviders = data?.missingScopesProviders || [];
  const syncErrors = Array.isArray(data?.syncErrors) ? data.syncErrors : [];

  const selectedTask = React.useMemo(
    () => topLevelTasks.find((task) => task.id === selectedTaskId) || null,
    [topLevelTasks, selectedTaskId]
  );

  const { data: subtasksData, isLoading: subtasksLoading } = useQuery(
    ['taskSubtasks', selectedTask?.id],
    async () => {
      const response = await api.get(`/tasks/${selectedTask.id}/subtasks`);
      return response.data.subtasks || [];
    },
    {
      enabled: open && !!selectedTask
    }
  );

  const subtasks = React.useMemo(
    () => subtasksData || selectedTask?.subtasks || [],
    [subtasksData, selectedTask]
  );

  React.useEffect(() => {
    if (!open || pinned || typeof document === 'undefined') {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [open, pinned]);

  React.useEffect(() => {
    if (!open) {
      setSelectedTaskId(null);
      setNewSubtaskTitle('');
    }
  }, [open]);

  React.useEffect(() => {
    if (selectedTaskId && !topLevelTasks.some((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(null);
    }
  }, [topLevelTasks, selectedTaskId]);

  React.useEffect(() => {
    if (!selectedTask) {
      setDraft({
        title: '',
        notes: '',
        dueDate: '',
        reminderAt: ''
      });
      return;
    }

    setDraft({
      title: selectedTask.title || '',
      notes: selectedTask.notes || '',
      dueDate: toDateInput(selectedTask.dueDate),
      reminderAt: toDateTimeLocal(selectedTask.reminderAt)
    });
  }, [selectedTask]);

  React.useEffect(() => {
    const nextDrafts = {};
    for (const subtask of subtasks) {
      nextDrafts[subtask.id] = subtask.title || '';
    }
    setSubtaskDrafts(nextDrafts);
  }, [subtasks]);

  const createSubtaskMutation = useMutation(
    async ({ taskId, title }) => {
      const response = await api.post(`/tasks/${taskId}/subtasks`, { title });
      return response.data.subtask;
    },
    {
      onSuccess: (_, variables) => {
        toast.success('Subtask added');
        setNewSubtaskTitle('');
        queryClient.invalidateQueries(['taskSubtasks', variables.taskId]);
        queryClient.invalidateQueries('tasks');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to add subtask';
        toast.error(message);
      }
    }
  );

  const updateSubtaskMutation = useMutation(
    async ({ taskId, subtaskId, payload }) => {
      const response = await api.put(`/tasks/${taskId}/subtasks/${subtaskId}`, payload);
      return response.data.subtask;
    },
    {
      onSuccess: (_, variables) => {
        toast.success('Subtask updated');
        queryClient.invalidateQueries(['taskSubtasks', variables.taskId]);
        queryClient.invalidateQueries('tasks');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to update subtask';
        toast.error(message);
      }
    }
  );

  const deleteSubtaskMutation = useMutation(
    async ({ taskId, subtaskId }) => {
      await api.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
      return { taskId, subtaskId };
    },
    {
      onSuccess: ({ taskId, subtaskId }) => {
        toast.success('Subtask removed');
        setSubtaskDrafts((prev) => {
          const next = { ...prev };
          delete next[subtaskId];
          return next;
        });
        queryClient.invalidateQueries(['taskSubtasks', taskId]);
        queryClient.invalidateQueries('tasks');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to remove subtask';
        toast.error(message);
      }
    }
  );

  const visibleTasks = React.useMemo(() => {
    const filtered = topLevelTasks.filter((task) => {
      if (statusFilter === 'completed') {
        return task.isCompleted;
      }
      if (statusFilter === 'open') {
        return !task.isCompleted;
      }
      return true;
    });

    const compareDirection = sortDirection === 'asc' ? 1 : -1;

    filtered.sort((a, b) => {
      if (sortBy === 'list') {
        const listCompare = a.listTitle.localeCompare(b.listTitle);
        if (listCompare !== 0) {
          return listCompare * compareDirection;
        }
      }

      if (sortBy === 'status') {
        if (a.isCompleted !== b.isCompleted) {
          return (a.isCompleted ? 1 : -1) * compareDirection;
        }
      }

      if (sortBy === 'date') {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        if (aTime !== bTime) {
          return (aTime - bTime) * compareDirection;
        }
      }

      const fallbackDate = (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) * -1;
      if (fallbackDate !== 0) {
        return fallbackDate;
      }

      return a.title.localeCompare(b.title);
    });

    return filtered;
  }, [topLevelTasks, sortBy, sortDirection, statusFilter]);

  const handleQuickCompleteToggle = (task) => {
    updateTaskMutation.mutate({
      id: task.id,
      playCompletionSound: !task.isCompleted,
      payload: {
        isCompleted: !task.isCompleted
      }
    });
  };

  const handleSave = () => {
    if (!selectedTask) {
      return;
    }

    if (!draft.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    updateTaskMutation.mutate({
      id: selectedTask.id,
      payload: {
        title: draft.title.trim(),
        notes: draft.notes,
        dueDate: draft.dueDate || null,
        reminderAt: draft.reminderAt || null,
        isCompleted: selectedTask.isCompleted
      }
    });
  };

  const handleCreateSubtask = () => {
    if (!selectedTask) {
      return;
    }

    const trimmedTitle = newSubtaskTitle.trim();
    if (!trimmedTitle) {
      toast.error('Subtask title is required');
      return;
    }

    createSubtaskMutation.mutate({
      taskId: selectedTask.id,
      title: trimmedTitle
    });
  };

  const handleSyncErrorAction = (syncError) => {
    const action = syncError?.action;
    if (!action) {
      openSettingsConnections();
      return;
    }

    if (action.type === 'external' && action.url) {
      window.open(action.url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (action.type === 'settings') {
      window.location.href = action.path || '/settings?tab=connections';
      return;
    }

    openSettingsConnections();
  };

  if (!open) {
    return null;
  }

  let lastListGroupKey = null;
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskId((currentTaskId) => (currentTaskId === taskId ? null : taskId));
  };

  return (
    <>
      {!pinned && <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />}
      <aside className="fixed inset-y-0 left-0 z-50 w-[min(92vw,42rem)] max-w-full overflow-hidden border-r border-gray-200 bg-white shadow-2xl lg:w-[42rem]">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-slate-100 p-2 text-slate-700">
                <FiCheckSquare className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Tasks</h2>
                <p className="text-xs text-gray-500">Microsoft To Do and Google Tasks</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onPinnedChange?.(!pinned)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  pinned
                    ? 'border-primary-200 bg-primary-50 text-primary-700 hover:border-primary-300 hover:bg-primary-100'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                {pinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                type="button"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isLoading}
                className="btn-secondary text-sm"
              >
                <FiRefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isLoading ? 'animate-spin' : ''}`} />
                Sync
              </button>
              <button type="button" onClick={onClose} className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <FiX className="h-5 w-5" />
              </button>
            </div>
          </div>

          {data?.needsReconnect && (
            <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-800">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-amber-900">Task access is not connected</p>
                  <p className="mt-1">
                    Connect task access from Settings for {reconnectProviders.join(' and ')} account{reconnectProviders.length > 1 ? 's' : ''}.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openSettingsConnections}
                  className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 transition hover:border-amber-400 hover:bg-amber-100"
                >
                  Open Connections
                </button>
              </div>
            </div>
          )}

          {syncErrors.length > 0 && (
            <div className="mx-5 mt-4 space-y-3">
              {syncErrors.map((result, index) => (
                <div
                  key={`${result.provider || 'provider'}-${result.accountId || index}-${result.errorCode || 'error'}`}
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-800"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FiAlertTriangle className="h-4 w-4 flex-shrink-0 text-red-600" />
                        <p className="font-semibold text-red-900">
                          {result.provider === 'google' ? 'Google Tasks issue' : result.provider === 'microsoft' ? 'Microsoft Tasks issue' : 'Task sync issue'}
                        </p>
                      </div>
                      <p className="mt-2">{result.error}</p>
                      {result.guidance && (
                        <p className="mt-1 text-red-700">{result.guidance}</p>
                      )}
                    </div>
                    {result.action && (
                      <button
                        type="button"
                        onClick={() => handleSyncErrorAction(result)}
                        className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 transition hover:border-red-400 hover:bg-red-100"
                      >
                        {result.action.type === 'external' ? <FiExternalLink className="h-3.5 w-3.5" /> : null}
                        {result.action.label || 'Fix'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-b border-gray-200 px-5 py-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Sort by</label>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="input-field text-sm">
                  <option value="date">Due date</option>
                  <option value="list">List</option>
                  <option value="status">Completion</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Order</label>
                <select value={sortDirection} onChange={(event) => setSortDirection(event.target.value)} className="input-field text-sm">
                  <option value="asc">ASC</option>
                  <option value="desc">DESC</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="input-field text-sm">
                  <option value="open">Open</option>
                  <option value="completed">Completed</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className={`min-h-0 ${selectedTask ? 'flex-[1.1] border-b border-gray-200' : 'flex-1'}`}>
              {isLoading ? (
                <div className="flex h-full items-center justify-center">
                  <LoadingSpinner />
                </div>
              ) : visibleTasks.length === 0 ? (
                <div className="px-5 py-8 text-sm text-gray-500">
                  {data?.syncInProgress
                    ? 'Initial task sync is running. FutureNote will load your tasks automatically when it finishes.'
                    : data?.syncCompletedDuringRequest
                      ? (data?.syncMessage || 'Task sync completed, but no tasks were returned by the provider.')
                      : 'No tasks found. Sync tasks or reconnect your accounts if task access was added recently.'}
                </div>
              ) : (
                <div className="h-full overflow-y-auto px-3 py-4">
                  {visibleTasks.map((task) => {
                    const listGroupKey = `${task.provider}:${task.listTitle}`;
                    const shouldShowListHeader = sortBy === 'list' && lastListGroupKey !== listGroupKey;
                    const isOverdue = isTaskOverdue(task);
                    const isDueToday = isTaskDueToday(task);
                    if (shouldShowListHeader) {
                      lastListGroupKey = listGroupKey;
                    }

                    return (
                      <React.Fragment key={task.id}>
                        {shouldShowListHeader && (
                          <div className="px-2 pb-2 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {task.listTitle} · {task.provider}
                          </div>
                        )}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleTaskSelection(task.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleTaskSelection(task.id);
                            }
                          }}
                          className={`mb-2 w-full rounded-xl border p-3 text-left transition ${
                            isOverdue
                              ? selectedTaskId === task.id
                                ? 'border-red-300 bg-red-100'
                                : 'border-red-200 bg-red-50 hover:border-red-300'
                              : isDueToday
                                ? selectedTaskId === task.id
                                  ? 'border-emerald-300 bg-emerald-100'
                                  : 'border-emerald-200 bg-emerald-50 hover:border-emerald-300'
                              : selectedTaskId === task.id
                                ? 'border-primary-300 bg-primary-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleQuickCompleteToggle(task);
                              }}
                              className={`mt-0.5 rounded-full ${task.isCompleted ? 'text-green-600' : 'text-gray-300 hover:text-primary-600'}`}
                              title={task.isCompleted ? 'Mark as open' : 'Mark as complete'}
                            >
                              {task.isCompleted ? <FiCheck className="h-5 w-5" /> : <FiCircle className="h-5 w-5" />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <h3 className={`text-sm font-medium ${task.isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                  {task.title}
                                </h3>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${task.provider === 'google' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                                  {task.provider}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">{task.listTitle}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                                <span className={isOverdue ? 'font-medium text-red-700' : isDueToday ? 'font-medium text-emerald-700' : ''}>
                                  {formatTaskDate(task.dueDate)}
                                </span>
                                <span>{task.isCompleted ? 'Completed' : 'Open'}</span>
                                <span className="inline-flex items-center gap-1">
                                  <FiClock className="h-3 w-3" />
                                  {formatReminder(task.reminderAt)}
                                </span>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-400">
                                <span>Created {formatTimestamp(task.createdAt)}</span>
                                <span>Edited {formatTimestamp(task.updatedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedTask && (
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Edit task</h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {selectedTask.provider === 'google'
                        ? 'Google Tasks reminders are stored locally in FutureNote.'
                        : 'Microsoft To Do reminders sync back to Microsoft.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 sm:grid-cols-2">
                    <div>
                      <p className="font-medium text-gray-700">Assigned list</p>
                      <p className="mt-1">{selectedTask.listTitle}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Created</p>
                      <p className="mt-1">{formatTimestamp(selectedTask.createdAt)}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Last edited</p>
                      <p className="mt-1">{formatTimestamp(selectedTask.updatedAt)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Title</label>
                    <input
                      value={draft.title}
                      onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
                      className="input-field text-sm"
                      maxLength={200}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
                    <textarea
                      value={draft.notes}
                      onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))}
                      className="input-field h-28 text-sm"
                      maxLength={5000}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Due date</label>
                    <input
                      type="date"
                      value={draft.dueDate}
                      onChange={(event) => setDraft((prev) => ({ ...prev, dueDate: event.target.value }))}
                      className="input-field text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Reminder</label>
                    <input
                      type="datetime-local"
                      value={draft.reminderAt}
                      onChange={(event) => setDraft((prev) => ({ ...prev, reminderAt: event.target.value }))}
                      className="input-field text-sm"
                    />
                    <button
                      type="button"
                      className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => setDraft((prev) => ({ ...prev, reminderAt: '' }))}
                    >
                      Clear reminder
                    </button>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-xs font-medium text-gray-700">Completion</p>
                      <p className="text-[11px] text-gray-500">
                        {selectedTask.isCompleted ? 'This task is completed' : 'This task is still open'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary text-sm"
                      onClick={() => handleQuickCompleteToggle(selectedTask)}
                    >
                      {selectedTask.isCompleted ? 'Mark open' : 'Mark complete'}
                    </button>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white">
                    <div className="border-b border-gray-200 px-3 py-3">
                      <h4 className="text-sm font-semibold text-gray-900">Subtasks</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        {selectedTask.provider === 'microsoft'
                          ? 'Microsoft checklist items for this task.'
                          : 'Google subtasks linked to this task.'}
                      </p>
                    </div>

                    <div className="space-y-3 px-3 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={newSubtaskTitle}
                          onChange={(event) => setNewSubtaskTitle(event.target.value)}
                          className="input-field text-sm"
                          placeholder="Add a new subtask"
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="btn-secondary inline-flex items-center text-sm"
                          onClick={handleCreateSubtask}
                          disabled={createSubtaskMutation.isLoading}
                        >
                          <FiPlus className="mr-2 h-4 w-4" />
                          Add
                        </button>
                      </div>

                      {subtasksLoading ? (
                        <div className="flex justify-center py-4">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : subtasks.length === 0 ? (
                        <div className="rounded-md bg-gray-50 px-3 py-3 text-xs text-gray-500">
                          No subtasks yet.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {subtasks.map((subtask) => {
                            const draftTitle = subtaskDrafts[subtask.id] ?? subtask.title;
                            const isUpdatingSubtask = updateSubtaskMutation.isLoading
                              && updateSubtaskMutation.variables?.subtaskId === subtask.id;
                            const isDeletingSubtask = deleteSubtaskMutation.isLoading
                              && deleteSubtaskMutation.variables?.subtaskId === subtask.id;

                            return (
                              <div key={subtask.id} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                                <div className="flex items-start gap-2">
                                  <button
                                    type="button"
                                    onClick={() => updateSubtaskMutation.mutate({
                                      taskId: selectedTask.id,
                                      subtaskId: subtask.id,
                                      payload: {
                                        title: draftTitle.trim() || subtask.title,
                                        isCompleted: !subtask.isCompleted
                                      }
                                    })}
                                    className={`mt-2 rounded-full ${subtask.isCompleted ? 'text-green-600' : 'text-gray-300 hover:text-primary-600'}`}
                                    title={subtask.isCompleted ? 'Mark as open' : 'Mark as complete'}
                                  >
                                    {subtask.isCompleted ? <FiCheck className="h-5 w-5" /> : <FiCircle className="h-5 w-5" />}
                                  </button>

                                  <div className="min-w-0 flex-1 space-y-2">
                                    <input
                                      value={draftTitle}
                                      onChange={(event) => setSubtaskDrafts((prev) => ({
                                        ...prev,
                                        [subtask.id]: event.target.value
                                      }))}
                                      className={`input-field text-sm ${subtask.isCompleted ? 'text-gray-500 line-through' : ''}`}
                                      maxLength={200}
                                    />
                                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                                      <span>Created {formatTimestamp(subtask.createdAt)}</span>
                                      <span>Edited {formatTimestamp(subtask.updatedAt)}</span>
                                      <span>{subtask.isCompleted ? 'Completed' : 'Open'}</span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="btn-secondary text-sm"
                                      onClick={() => updateSubtaskMutation.mutate({
                                        taskId: selectedTask.id,
                                        subtaskId: subtask.id,
                                        payload: {
                                          title: (draftTitle || '').trim() || subtask.title,
                                          isCompleted: subtask.isCompleted
                                        }
                                      })}
                                      disabled={isUpdatingSubtask}
                                    >
                                      {isUpdatingSubtask ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded-md border border-red-200 bg-white p-2 text-red-500 hover:bg-red-50 hover:text-red-600"
                                      onClick={() => deleteSubtaskMutation.mutate({
                                        taskId: selectedTask.id,
                                        subtaskId: subtask.id
                                      })}
                                      disabled={isDeletingSubtask}
                                      title="Remove subtask"
                                    >
                                      <FiTrash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        if (!selectedTask) {
                          return;
                        }
                        setDraft({
                          title: selectedTask.title || '',
                          notes: selectedTask.notes || '',
                          dueDate: toDateInput(selectedTask.dueDate),
                          reminderAt: toDateTimeLocal(selectedTask.reminderAt)
                        });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleSave}
                      disabled={updateTaskMutation.isLoading}
                    >
                      {updateTaskMutation.isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default TasksDrawer;
